/* Botanical Portfolio — tilted organic mosaic with leaf flourishes
   between sections. Each photo rounded + slightly rotated for a
   hand-pressed pressed-flower-album feel. */

import { useEffect } from "react";
import { Leaf } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { getFocalStyle } from "@/lib/focal";
import { optimizeImageUrl } from "@/lib/imageOptim";
import { EditorialSkeleton } from "../editorial/_EditorialSkeleton";

const TILTS = ["", "-rotate-1", "rotate-1", "-rotate-[0.5deg]", "rotate-[0.5deg]"];

export default function BotanicalPortfolio() {
  const { settings, loading } = useSiteSettings();
  const port = settings?.portfolio_images || [];
  const focals = settings?.portfolio_focal_points || [];
  const studioName = settings?.studio_name || "Studio";
  useEffect(() => { document.title = `${studioName} — The Garden`; }, [studioName]);
  if (loading && !settings) return <EditorialSkeleton eyebrow="Tending the garden." />;

  return (
    <div data-testid="botanical-portfolio" style={{ background: "var(--tenant-bg, #F6F4EE)", color: "var(--tenant-fg, #2A3A20)" }}>

      <section className="px-6 lg:px-12 pt-16 pb-12 text-center">
        <div className="inline-flex items-center gap-3 mb-5">
          <Leaf className="w-5 h-5" strokeWidth={1.25} style={{ color: "var(--tenant-accent)" }} />
          <div className="text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>
            The Garden
          </div>
          <Leaf className="w-5 h-5 -scale-x-100" strokeWidth={1.25} style={{ color: "var(--tenant-accent)" }} />
        </div>
        <h1 style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.5rem, 5.5vw, 4.5rem)" }}>
          <em className="italic">{settings?.portfolio_title || "Recent blooms."}</em>
        </h1>
      </section>

      <div className="max-w-[1280px] mx-auto px-6 lg:px-12 pb-32">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
          {port.map((src, i) => (
            <figure key={i} className={`transform ${TILTS[i % TILTS.length]} transition-transform hover:rotate-0 hover:scale-[1.03]`}>
              <img src={optimizeImageUrl(src, { w: 900 })} alt=""
                className="w-full aspect-[4/5] object-cover rounded-[24px]"
                style={getFocalStyle(focals, i)}
                loading={i < 3 ? "eager" : "lazy"} decoding="async" />
              <figcaption className="text-center mt-3 text-[10px] uppercase tracking-[0.32em]"
                          style={{ color: "var(--tenant-accent)" }}>
                ❀ Bloom №{String(i + 1).padStart(2, "0")}
              </figcaption>
            </figure>
          ))}
        </div>
        {port.length === 0 && (
          <p className="text-center italic mt-12" style={{ color: "var(--tenant-fg-soft)" }}>
            The garden is still in seed. Blooms forthcoming.
          </p>
        )}
      </div>
    </div>
  );
}
